import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Body1,
  Avatar,
  CrownBlankIcon,
  CrownSelectedIcon,
  Tooltip,
  CancelCrossIcon,
  IconButton,
} from "@activecollab/components";
import PropTypes from "prop-types";
import { replaceAvatarSize } from "~/utils/utils.jsx";
import { useConversationContext } from "../../../context/index.jsx";
import { ListItem } from "../../List/ListItem.jsx";
import { types } from "../types/index.jsx";

export const ParticipantItem = ({
  id,
  avatarUrl,
  name,
  isAdmin,
  onAddConversationAdmin,
  onRemoveConversationAdmin,
  onRemove,
  onLeave,
}) => {
  const { canManage = false, loggedUserId = 0, conversation = {} } = useConversationContext();
  const { t } = useTranslation();

  const { class: classType } = conversation;
  const type = types[classType];

  const handleAddConversationAdmin = useCallback(() => {
    canManage && typeof onAddConversationAdmin === "function" && onAddConversationAdmin(id);
  }, [canManage, onAddConversationAdmin, id]);

  const handleRemoveConversationAdmin = useCallback(() => {
    canManage && typeof onRemoveConversationAdmin === "function" && onRemoveConversationAdmin(id);
  }, [canManage, onRemoveConversationAdmin, id]);

  const handleRemoveParticipant = useCallback(() => {
    if (loggedUserId === id) {
      typeof onLeave === "function" && onLeave();
    } else if (canManage && loggedUserId !== id) {
      typeof onRemove === "function" && onRemove(id);
    }
  }, [canManage, onRemove, id, loggedUserId, onLeave]);

  return (
    <ListItem className="ac-cursor-default tw-bg-transparent">
      <Avatar
        url={replaceAvatarSize(avatarUrl, 40)}
        size={36}
        alt={name}
        className="tw-shrink-0"
      />
      <div className="tw-ml-4 tw-text-base tw-min-w-0 tw-grow tw-flex tw-items-center tw-justify-between">
        <Body1
          weight="bold"
          className="tw-truncate"
        >
          {name}
        </Body1>
        <div className="tw-flex tw-items-center">
          {isAdmin ? (
            <Tooltip title={canManage ? t("Revoke Admin") : t("Admin")}>
              {canManage ? (
                <IconButton
                  variant="text gray"
                  onClick={handleRemoveConversationAdmin}
                >
                  <CrownSelectedIcon className="selected-crown-icon" />
                </IconButton>
              ) : (
                <CrownSelectedIcon className="selected-crown-icon tw-mx-1" />
              )}
            </Tooltip>
          ) : canManage ? (
            <Tooltip title={t("Make Admin")}>
              <IconButton
                variant="text gray"
                onClick={handleAddConversationAdmin}
              >
                <CrownBlankIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          {(canManage || loggedUserId === id) && type === "GroupConversation" ? (
            <Tooltip title={loggedUserId === id ? t("Leave") : t("Remove participant")}>
              <IconButton
                variant="text gray"
                onClick={handleRemoveParticipant}
              >
                <CancelCrossIcon />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </ListItem>
  );
};

ParticipantItem.propTypes = {
  id: PropTypes.number.isRequired,
  avatarUrl: PropTypes.string,
  name: PropTypes.string,
  isAdmin: PropTypes.bool,
  onAddConversationAdmin: PropTypes.func,
  onRemoveConversationAdmin: PropTypes.func,
  onRemove: PropTypes.func,
  onLeave: PropTypes.func,
};

ParticipantItem.defaultProps = {
  avatarUrl: "#",
  name: "",
  isAdmin: false,
};
